import { faTimes } from "@jewlr/pro-regular-svg-icons/faTimes"
import PropTypes from "prop-types"
import queryString from "query-string"
import { useCallback } from "react"
import { Link, useLocation } from "react-router-dom"

import useCollection from "areas/collection/hoc/use-collection"
import Button from "components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "components/ui/carousel"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Image from "components/ui/image"
import { trackSuperFiltersEvent } from "helpers/collection"
import { getImgSrcSet } from "helpers/image"

import withFilters from "./hoc/with-filters"

const getFilterParams = (url) => {
  if (!url?.startsWith("?")) return null

  const filterParams = queryString.parse(url, {
    arrayFormat: "bracket",
  })?.filtered_tags
  if (!filterParams) return null

  return Array.isArray(filterParams) ? filterParams : [filterParams]
}

const SuperFilters = ({ appliedFilters, removeFilter, setFilter }) => {
  const location = useLocation()
  const {
    data: {
      applied_filters: apiAppliedFilters = [],
      super_filters: superFilters,
    } = {},
  } = useCollection()

  const applied_filters = appliedFilters ?? apiAppliedFilters

  const sanitizeLinks = useCallback(
    (url) => {
      if (!url) return null

      if (url.startsWith("?")) {
        return `${location.pathname}${url}`
      } else if (!url.startsWith("http") && !url.startsWith("/")) {
        return `/${url}`
      }

      return url
    },
    [location.pathname]
  )

  const trackClick = useCallback(
    (url) => trackSuperFiltersEvent(location.pathname, url),
    [location.pathname]
  )

  const handleLinkClick = useCallback(
    (e, filterUrl) => {
      trackClick(filterUrl)
      const filterParams = getFilterParams(filterUrl)
      if (filterParams) {
        e.preventDefault()
        setFilter(filterParams, false, "all")
      }
    },
    [setFilter, trackClick]
  )

  if (!superFilters) return null

  if (superFilters.type === "carousel") {
    return (
      <Carousel
        aria-label="Quick Filters"
        className="tablet:mx-0 -mx-16"
        disableSkip
        opts={{
          loop: false,
          slidesToScroll: "auto",
        }}
        role="navigation"
      >
        <CarouselContent className="tablet:px-0 gap-8 px-16">
          {superFilters.links.map((filter, index) => (
            <CarouselItem
              aria-roledescription={undefined}
              className="tablet:basis-130 basis-1/4"
              key={`super-filters-${filter.name}-${index}}`}
              role={undefined}
            >
              <Link
                className="block"
                data-lc="super-filters"
                onClick={(e) => handleLinkClick(e, filter.url)}
                to={sanitizeLinks(filter.url)}
              >
                {filter.image && (
                  <Image
                    alt=""
                    className="h-auto w-full"
                    fallback={filter.image.fallback}
                    height={filter.image.height}
                    srcSet={getImgSrcSet(filter.image.src, filter.image.width)}
                    width={filter.image.width}
                  />
                )}
                <span className="text-13 mt-8 font-semibold">
                  {filter.name}
                </span>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="tablet:inline-flex" />
        <CarouselNext className="tablet:inline-flex" />
      </Carousel>
    )
  }

  return (
    <nav
      aria-label="Quick Filters"
      className="no-scrollbar desktop:flex-wrap desktop:justify-start desktop:overflow-x-auto desktop:overflow-y-auto desktop:gap-x-10 flex gap-x-6 gap-y-10 overflow-x-scroll whitespace-nowrap"
    >
      {superFilters.links.map((filter, index) => {
        const key = `super-filters-${filter.name}-${index}}`
        const filterParams = getFilterParams(filter.url)
        const isActive =
          filterParams?.length > 0 &&
          filterParams.every((param) => applied_filters.includes(param))

        if (isActive) {
          return (
            <span
              aria-current="true"
              className="bg-black-print leading-25 desktop:text-18 border-black-print inline-flex items-center gap-6 border px-20 font-semibold text-white"
              key={key}
            >
              {filter.name}
              <Button
                aria-label={`Remove ${filter.name} filter`}
                className="h-16 p-4"
                onClick={(e) => removeFilter(e, filterParams)}
                variant="primary"
              >
                <FontAwesomeIcon
                  className="text-12 text-white"
                  icon={faTimes}
                />
              </Button>
            </span>
          )
        }

        return (
          <Button
            asChild
            className="border-grey-20 desktop:text-18 leading-25 inline-flex items-center border px-20 font-semibold focus-visible:-outline-offset-2"
            key={key}
            size="sm"
            variant="tertiary"
          >
            <Link
              data-lc="super-filters"
              onClick={(e) => handleLinkClick(e, filter.url)}
              to={sanitizeLinks(filter.url)}
            >
              {filter.name}
            </Link>
          </Button>
        )
      })}
    </nav>
  )
}

SuperFilters.propTypes = {
  appliedFilters: PropTypes.array,
  removeFilter: PropTypes.func,
  setFilter: PropTypes.func,
}

export default withFilters(SuperFilters)
